import { io } from "socket.io-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button.jsx";
import { connectSocket, releaseSocket } from "../../lib/socket-client.js"
import { Mic, MicOff, Monitor, PhoneOff, Share2, Video as VideoIcon, VideoOff } from "lucide-react";

function formatDuration(seconds){
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export default function MultiPeerCall({ room, displayName }){
  const socketRef = useRef(null);
  const localStreamRef = useRef(null);
  const screensharingRef = useRef(false);
  const peersRef = useRef(new Map());
  const autoDialedRef = useRef(new Set());
  const [me, setMe] = useState("");
  const [online, setOnline] = useState([]);
  const [names, setNames] = useState({});
  const [active, setActive] = useState(null);
  const [includeSystemAudio, setIncludeSystemAudio] = useState(true);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreensharing, setIsScreensharing] = useState(false);
  const [screenSharer, setScreenSharer] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [, forceTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => forceTick((x) => x + 1), 800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setCallDuration((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const videos = useMemo(() => {
    const list = [];
    list.push({ id: me || "me", stream: localStreamRef.current, isLocal: true, name: displayName || "You" });
    for (const [id, entry] of peersRef.current.entries()) {
      list.push({ id, stream: entry.stream, isLocal: false, name: names[id] || id.slice(0, 6) });
    }
    return list;
  }, [me, forceTick, names, displayName]);

  useEffect(() => {
    const socket = connectSocket();
    socketRef.current = socket;

    const handleConnect = () => setMe(socket.id);
    socket.on("connect", handleConnect);

    socket.emit("room:join", { room, profile: { name: displayName || "You" } });
    socket.emit("who", room, (list) => {
      const entries = Array.isArray(list) ? list : [];
      setOnline(entries.map((item) => (typeof item === "string" ? item : item?.id)).filter(Boolean));
      setNames((cur) => {
        const next = { ...cur };
        for (const item of entries) {
          if (item && typeof item === "object" && item.id && item.name) next[item.id] = item.name;
        }
        return next;
      });
    });

    const handlePresence = (ev) => {
      if (!ev || !ev.userId) return;
      if (ev.type === "join") {
        setOnline((cur) => Array.from(new Set([...cur, ev.userId])).filter(Boolean));
        if (ev.name) setNames((cur) => ({ ...cur, [ev.userId]: ev.name }));
      }
      if (ev.type === "leave") {
        setOnline((cur) => cur.filter((id) => id !== ev.userId));
        setNames((cur) => {
          const next = { ...cur };
          delete next[ev.userId];
          return next;
        });
        autoDialedRef.current.delete(ev.userId);
      }
    };

    const handleCallMade = async ({ from, signal }) => {
      if (from) autoDialedRef.current.add(from);
      try {
        let entry = peersRef.current.get(from);
        if (!entry) {
          const pc = createPC(from);
          peersRef.current.set(from, { pc, stream: null });
          entry = peersRef.current.get(from);
        }
        await entry.pc.setRemoteDescription(new RTCSessionDescription(signal));
        const answer = await entry.pc.createAnswer();
        await entry.pc.setLocalDescription(answer);
        socket.emit("answer-call", { to: from, signal: entry.pc.localDescription });
      } catch {}
    };

    const handleCallAccepted = async (signal) => {
      for (const [, entry] of peersRef.current) {
        if (!entry.pc.currentRemoteDescription) {
          try {
            await entry.pc.setRemoteDescription(new RTCSessionDescription(signal));
          } catch {}
        }
      }
    };

    const handleIceCandidate = async ({ from, candidate }) => {
      try {
        const entry = peersRef.current.get(from);
        if (entry) await entry.pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch {}
    };

    const handlePeerEnded = ({ from }) => {
      endPeer(from);
    };

    const handleScreenShareStarted = (payload = {}) => {
      setScreenSharer(payload.sharerId || null);
    };
    const handleScreenShareStopped = (payload = {}) => {
      const id = payload.sharerId || null;
      setScreenSharer((prev) => {
        if (!prev) return null;
        if (!id || prev === id) return null;
        return prev;
      });
    };

    socket.on("presence", handlePresence);
    socket.on("call-made", handleCallMade);
    socket.on("call-accepted", handleCallAccepted);
    socket.on("ice-candidate", handleIceCandidate);
    socket.on("peer-ended", handlePeerEnded);
    socket.on("screen-share-started", handleScreenShareStarted);
    socket.on("screen-share-stopped", handleScreenShareStopped);

    return () => {
      socket.emit("room:leave", room);
      socket.off("connect", handleConnect);
      socket.off("presence", handlePresence);
      socket.off("call-made", handleCallMade);
      socket.off("call-accepted", handleCallAccepted);
      socket.off("ice-candidate", handleIceCandidate);
      socket.off("peer-ended", handlePeerEnded);
      socket.off("screen-share-started", handleScreenShareStarted);
      socket.off("screen-share-stopped", handleScreenShareStopped);
      for (const [, entry] of peersRef.current) entry.pc.close();
      peersRef.current.clear();
      releaseSocket();
    };
  }, [room, displayName]);

  useEffect(() => {
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        localStreamRef.current = stream;
        const hasVideo = stream.getVideoTracks().some((track) => track.enabled);
        const hasAudio = stream.getAudioTracks().some((track) => track.enabled);
        setIsCameraOff(!hasVideo);
        setIsMicMuted(!hasAudio);
        monitorAudio("me", stream);
        forceTick((x) => x + 1);
      } catch {}
    })();
  }, []);

  const monitorAudio = (id, stream) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ac = new AC();
      const analyser = ac.createAnalyser();
      analyser.fftSize = 512;
      const src = ac.createMediaStreamSource(stream);
      src.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        analyser.getByteFrequencyData(buf);
        const avg = buf.reduce((a, b) => a + b, 0) / (buf.length || 1);
        if (avg > 22) setActive(id);
        requestAnimationFrame(loop);
      };
      loop();
    } catch {}
  };

  const others = online.filter((id) => id && id !== me);

  useEffect(() => {
    if (!me) return;
    const targets = online.filter((id) => id && id !== me);
    for (const id of targets) {
      if (!peersRef.current.has(id) && !autoDialedRef.current.has(id)) {
        callUser(id);
        autoDialedRef.current.add(id);
      }
    }
    for (const id of Array.from(autoDialedRef.current)) {
      if (!targets.includes(id)) autoDialedRef.current.delete(id);
    }
  }, [online, me]);

  function createPC(targetId){
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    pc.onicecandidate = (ev) => {
      if (ev.candidate && socketRef.current) {
        socketRef.current.emit("ice-candidate", { to: targetId, candidate: ev.candidate });
      }
    };
    pc.ontrack = (ev) => {
      const [ms] = ev.streams;
      const exists = peersRef.current.get(targetId);
      const entry = exists || { pc, stream: null };
      entry.stream = ms;
      peersRef.current.set(targetId, entry);
      monitorAudio(targetId, ms);
      forceTick((x) => x + 1);
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "disconnected", "closed"].includes(pc.connectionState)) endPeer(targetId);
    };
    return pc;
  }

  async function callUser(targetId){
    if (!targetId || peersRef.current.has(targetId)) return;
    const socket = socketRef.current;
    if (!socket) return;
    const pc = createPC(targetId);
    peersRef.current.set(targetId, { pc, stream: null });
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit("call-user", { userToCall: targetId, signalData: pc.localDescription, from: me });
    } catch {}
  }

  function endPeer(targetId){
    const entry = peersRef.current.get(targetId);
    if (!entry) return;
    try { entry.pc.close(); } catch {}
    peersRef.current.delete(targetId);
    autoDialedRef.current.delete(targetId);
    forceTick((x) => x + 1);
    socketRef.current?.emit("end-peer", { to: targetId });
  }

  async function toggleScreenShare(){
    if (!screensharingRef.current){
      try {
        const display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: includeSystemAudio ? { systemAudio: "include" } : false });
        const vTrack = display.getVideoTracks()[0];
        const aTrack = display.getAudioTracks()[0] || null;
        for (const [, entry] of peersRef.current) {
          const vSender = entry.pc.getSenders().find((s) => s.track && s.track.kind === "video");
          if (vSender) await vSender.replaceTrack(vTrack);
          if (aTrack) {
            const aSender = entry.pc.getSenders().find((s) => s.track && s.track.kind === "audio");
            if (aSender) await aSender.replaceTrack(aTrack);
            else entry.pc.addTrack(aTrack, new MediaStream([aTrack]));
          }
        }
        const baseAudios = localStreamRef.current?.getAudioTracks() || [];
        localStreamRef.current = new MediaStream([vTrack, ...(aTrack ? [aTrack] : baseAudios)]);
        screensharingRef.current = true;
        setIsScreensharing(true);
        setScreenSharer(me || socketRef.current?.id || "me");
        forceTick((x) => x + 1);
        socketRef.current?.emit("screen-share-started", { sharerId: me || socketRef.current?.id || "me" });
        vTrack.onended = () => stopShare();
      } catch {}
    } else {
      await stopShare();
    }
  }

  async function stopShare(){
    try {
      const cam = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      for (const [, entry] of peersRef.current) {
        const sender = entry.pc.getSenders().find((s) => s.track && s.track.kind === "video");
        if (sender) await sender.replaceTrack(cam.getVideoTracks()[0]);
      }
      localStreamRef.current = cam;
      screensharingRef.current = false;
      setIsScreensharing(false);
      setScreenSharer(null);
      forceTick((x) => x + 1);
      socketRef.current?.emit("screen-share-stopped", { sharerId: me || socketRef.current?.id || "me" });
    } catch {}
  }

  const toggleMic = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !isMicMuted;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !next;
    });
    setIsMicMuted(next);
  };

  const toggleCamera = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !isCameraOff;
    stream.getVideoTracks().forEach((track) => {
      track.enabled = !next;
    });
    setIsCameraOff(next);
  };

  const hangUp = () => {
    try { socketRef.current?.disconnect(); } catch {}
    for (const [, entry] of peersRef.current) {
      try { entry.pc.close(); } catch {}
    }
    peersRef.current.clear();
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    setScreenSharer(null);
    window.location.href = "/";
  };

  const shareLink = async () => {
    const link = `${window.location.origin}/call?room=${encodeURIComponent(room || me || "call")}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {}
  };

  return (
    <div className="flex min-h-[80vh] flex-col bg-[#0b141a] text-[#e9edef]">
      <header className="flex flex-wrap items-center gap-4 border-b border-[#1f2c34] px-6 py-4">
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold">Group call</div>
          <div className="text-sm text-[#8696a0]">{videos.length} participants • {formatDuration(callDuration)}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" className="bg-[#202c33] text-[#e9edef] hover:bg-[#1f2c34]" onClick={shareLink}>
            Share link
          </Button>
          <Button variant="secondary" size="sm" className="bg-[#202c33] text-[#e9edef] hover:bg-[#1f2c34]" onClick={() => setIncludeSystemAudio((v) => !v)}>
            System audio: {includeSystemAudio ? "on" : "off"}
          </Button>
        </div>
      </header>

      <main className="flex flex-1 flex-col gap-4 px-4 py-6 sm:px-8">
        <div className="grid flex-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {videos.map((v) => {
            const hasVideo = !!(v.stream && v.stream.getVideoTracks().some((t) => t.readyState === "live" && t.enabled));
            const hasAudio = !!(v.stream && v.stream.getAudioTracks().some((t) => t.readyState === "live" && t.enabled));
            const isActive = active === v.id;
            const isSharing = screenSharer && screenSharer === v.id;
            return (
              <div
                key={v.id}
                className={`relative overflow-hidden rounded-3xl border border-[#1f2c34] bg-[#111b21] shadow-lg ${isActive || isSharing ? "ring-2 ring-[#25d366]" : ""}`}
              >
                <video
                  autoPlay
                  playsInline
                  muted={v.isLocal || isMicMuted}
                  ref={(el) => {
                    if (el && v.stream) el.srcObject = v.stream;
                  }}
                  className={`h-full w-full object-cover transition ${hasVideo ? "opacity-100" : "opacity-20"}`}
                />
                {isSharing && (
                  <div className="absolute left-4 top-4 rounded-full bg-[#25d366] px-3 py-1 text-xs font-semibold text-[#0b141a] shadow-lg">
                    Sharing screen
                  </div>
                )}
                {!hasVideo && (
                  <div className="absolute inset-0 grid place-items-center text-4xl text-[#25d366]">
                    {v.name?.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-[#0b141a]/90 to-transparent px-4 py-3 text-sm">
                  <div>
                    <div className="font-semibold">{v.isLocal ? (displayName || "You") : (names[v.id] || v.name)}</div>
                    <div className="text-[11px] text-[#8696a0]">{hasAudio ? "Speaking" : "Muted"}</div>
                  </div>
                  <div className="flex items-center gap-2 text-[#e9edef]">
                    <span className={`grid h-7 w-7 place-items-center rounded-full ${hasVideo ? "bg-[#25d366]" : "bg-[#202c33]"}`}>📷</span>
                    <span className={`grid h-7 w-7 place-items-center rounded-full ${hasAudio ? "bg-[#25d366]" : "bg-[#202c33]"}`}>🎤</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-3xl border border-[#1f2c34] bg-[#111b21] p-4">
          <div className="text-sm font-semibold text-[#cfd4d8]">Connected</div>
          <div className="mt-2 flex flex-wrap gap-2 text-sm text-[#8696a0]">
            {others.length === 0 && <span>No additional participants yet.</span>}
            {others.map((id) => (
              <span key={id} className="rounded-full bg-[#202c33] px-3 py-1 text-[#e9edef]">
                {names[id] || id.slice(0, 6)}
              </span>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-[#1f2c34] px-4 py-5 sm:px-10">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={toggleMic}
            className={`h-14 w-14 rounded-full text-white transition ${isMicMuted ? "bg-[#f15a4f]" : "bg-[#25d366]"}`}
            aria-label={isMicMuted ? "Unmute microphone" : "Mute microphone"}
          >
            {isMicMuted ? <MicOff className="mx-auto h-6 w-6" /> : <Mic className="mx-auto h-6 w-6" />}
          </button>
          <button
            type="button"
            onClick={toggleCamera}
            className={`h-14 w-14 rounded-full text-white transition ${isCameraOff ? "bg-[#f15a4f]" : "bg-[#25d366]"}`}
            aria-label={isCameraOff ? "Turn camera on" : "Turn camera off"}
          >
            {isCameraOff ? <VideoOff className="mx-auto h-6 w-6" /> : <VideoIcon className="mx-auto h-6 w-6" />}
          </button>
          <button
            type="button"
            onClick={toggleScreenShare}
            className={`h-14 w-14 rounded-full text-white transition ${isScreensharing ? "bg-[#25d366]" : "bg-[#202c33]"}`}
            aria-label={isScreensharing ? "Stop sharing" : "Start sharing"}
          >
            {isScreensharing ? <Monitor className="mx-auto h-6 w-6" /> : <Share2 className="mx-auto h-6 w-6" />}
          </button>
          <button
            type="button"
            onClick={hangUp}
            className="h-16 w-16 rounded-full bg-[#f15a4f] text-white shadow-lg"
            aria-label="End call"
          >
            <PhoneOff className="mx-auto h-7 w-7" />
          </button>
        </div>
      </footer>
    </div>
  );
}
